<template>
  <div class="admin-page">
    <admin-page-header
      eyebrow="Ayarlar"
      title="Kategori yönetimi"
      description="Kampanya eşleşmelerinde kullanılan kategori kayıtlarını, görselleriyle birlikte tek akışta yönetin."
    />
    <v-alert
      v-if="loadError"
      type="error"
      dense
      outlined
    >
      {{ loadError }}<v-btn
        text
        small
        @click="fetchCategories"
      >
        Yeniden dene
      </v-btn>
    </v-alert>

    <v-card
      class="admin-table-card"
      outlined
    >
      <v-data-table
        dense
        :loading="loading"
        loading-text="Yükleniyor"
        :headers="headers"
        :items="categories"
        class="admin-data-table elevation-0"
      >
        <template v-slot:top>
          <v-toolbar
            dense
            :elevation="0"
            class="admin-page-toolbar"
          >
            <v-toolbar-title>Kategoriler</v-toolbar-title>
            <v-divider
              class="mx-4"
              inset
              vertical
            ></v-divider>
            <v-spacer></v-spacer>
            <v-dialog
              v-model="dialog"
              max-width="520px"
              persistent
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  color="primary"
                  v-on="on"
                >
                  Yeni Kategori
                </v-btn>
              </template>
              <v-card
                class="admin-dialog-card"
                outlined
              >
                <v-toolbar
                  dense
                  :elevation="0"
                >
                  <v-toolbar-title>{{ formTitle }}</v-toolbar-title>
                  <v-spacer></v-spacer>
                  <v-btn
                    icon
                    :disabled="saving"
                    aria-label="Kategori formunu kapat"
                    title="Kategori formunu kapat"
                    @click="close"
                  >
                    <v-icon>{{ icons.mdiClose }}</v-icon>
                  </v-btn>
                </v-toolbar>
                <v-card-text>
                  <v-alert
                    v-if="formError"
                    type="error"
                    dense
                    outlined
                  >
                    {{ formError }}
                  </v-alert>
                  <v-container>
                    <v-row>
                      <v-col cols="12">
                        <v-text-field
                          v-model="editedItem.name"
                          outlined
                          dense
                          label="Kategori Adı"
                          placeholder="Kategori adı"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12">
                        <div class="d-flex flex-column align-center">
                          <v-card
                            v-if="imagePreview || editedItem.imageUrl"
                            outlined
                            class="admin-surface pa-4 mb-4"
                            width="220"
                          >
                            <v-img
                              :src="imagePreview || editedItem.imageUrl"
                              max-width="180"
                              max-height="180"
                              contain
                              class="mx-auto"
                            ></v-img>
                          </v-card>
                          <input
                            ref="fileInput"
                            type="file"
                            accept="image/*"
                            style="display: none"
                            @change="onImageSelected"
                          />
                          <v-btn
                            color="primary"
                            outlined
                            @click="$refs.fileInput.click()"
                          >
                            <v-icon left>
                              {{ icons.mdiUpload }}
                            </v-icon>
                            {{ selectedImage ? 'Görseli Değiştir' : 'Görsel Yükle' }}
                          </v-btn>
                          <span
                            v-if="selectedImage"
                            class="mt-2 text-caption"
                          >
                            {{ selectedImage.name }}
                          </span>
                        </div>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn
                    color="primary"
                    text
                    :disabled="saving"
                    @click="close"
                  >
                    Çık
                  </v-btn>
                  <v-btn
                    color="primary"
                    :loading="saving"
                    :disabled="saving"
                    @click="save"
                  >
                    Kaydet
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar>
        </template>

        <template v-slot:item.imageUrl="{ item }">
          <v-avatar
            size="42"
            class="my-2"
          >
            <v-img
              :src="item.imageUrl || (item.image && item.image.localUrl) || ''"
              contain
            ></v-img>
          </v-avatar>
        </template>

        <template v-slot:item.created="{ item }">
          {{ formatDate(item.created) }}
        </template>

        <template v-slot:item.modified="{ item }">
          {{ formatDate(item.modified) }}
        </template>

        <template v-slot:item.action="{ item }">
          <v-btn
            icon
            small
            :aria-label="`${item.name} düzenle`"
            title="Düzenle"
            @click="editItem(item)"
          >
            <v-icon
              small
              color="info"
            >
              {{ icons.mdiPencil }}
            </v-icon>
          </v-btn>
          <v-btn
            icon
            small
            :aria-label="`${item.name} sil`"
            title="Sil"
            @click="openDeleteDialog(item)"
          >
            <v-icon
              small
              color="error"
            >
              {{ icons.mdiDelete }}
            </v-icon>
          </v-btn>
        </template>
      </v-data-table>

      <v-dialog
        v-model="deleteDialog"
        max-width="480px"
        persistent
      >
        <v-card class="admin-dialog-card">
          <v-card-title class="text-h5">
            Kategoriyi Sil
          </v-card-title>
          <v-card-text>
            <strong>{{ deleteItem.name }}</strong> kategorisini silmek istediğinizden emin misiniz?
            <p class="mt-3">
              {{ usageMessage }}
            </p>
            <v-alert
              v-if="deleteError"
              type="error"
              dense
              outlined
            >
              {{ deleteError }}
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              text
              :disabled="deleting"
              @click="closeDeleteDialog"
            >
              İptal
            </v-btn>
            <v-btn
              color="error"
              :loading="deleting"
              :disabled="deleting || usageBlocked"
              @click="confirmDelete"
            >
              Sil
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </div>
</template>

<script>
import axios from 'axios'
import {
  mdiPencil, mdiClose, mdiDelete, mdiUpload,
} from '@mdi/js'
import { resolveApiErrorMessage } from '@/utils/apiErrors'
import { getAccessToken } from '@/utils/panelSession'
import AdminPageHeader from '@/components/admin/AdminPageHeader.vue'

export default {
  name: 'CategoriesList',
  components: {
    AdminPageHeader,
  },
  data() {
    return {
      categories: [],
      loading: false,
      loadError: '',
      formError: '',
      saving: false,
      deleting: false,
      deleteError: '',
      usageMessage: '',
      usageBlocked: true,
      headers: [
        { text: 'Eylemler', value: 'action', sortable: false },
        { text: 'ID', value: 'id', sortable: true },
        { text: 'Görsel', value: 'imageUrl', sortable: false },
        { text: 'Kategori Adı', value: 'name', sortable: true },
        { text: 'Oluşturulma Tarihi', value: 'created', sortable: true },
        { text: 'Güncellenme Tarihi', value: 'modified', sortable: true },
      ],
      dialog: false,
      editedIndex: -1,
      editedItem: {
        id: 0,
        name: '',
      },
      defaultItem: {
        id: 0,
        name: '',
      },
      requestOptions: {
        headers: { Authorization: `Bearer ${getAccessToken()}` },
      },
      icons: {
        mdiPencil,
        mdiClose,
        mdiDelete,
        mdiUpload,
      },
      deleteDialog: false,
      deleteItem: {},
      selectedImage: null,
      imagePreview: null,
    }
  },
  computed: {
    formTitle() {
      return this.editedIndex === -1 ? 'Yeni Kategori' : 'Kategoriyi Güncelle'
    },
  },
  watch: {
    dialog(val) {
      if (!val) {
        this.close()
      }
    },
  },
  mounted() {
    this.fetchCategories()
  },
  methods: {
    normalizeCategoryList(payload) {
      if (Array.isArray(payload)) return payload
      if (Array.isArray(payload?.data)) return payload.data
      if (Array.isArray(payload?.list)) return payload.list
      if (Array.isArray(payload?.content)) return payload.content
      if (Array.isArray(payload?.items)) return payload.items

      return []
    },
    normalizeCategoryItem(item) {
      return {
        ...item,
        image: item?.image || null,
        imageUrl: item?.imageUrl || item?.image?.localUrl || '',
      }
    },
    async fetchCategories() {
      this.loading = true
      this.loadError = ''
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}category`,
          this.requestOptions,
        )

        const payload = response?.data?.data ?? response?.data
        const categories = this.normalizeCategoryList(payload).map(item => this.normalizeCategoryItem(item))

        if (response?.status === 200 || response?.data?.status === 200 || response?.data?.status === 0) {
          this.categories = categories
        }

        return this.categories
      } catch (error) {
        this.loadError = resolveApiErrorMessage(error, 'Kategoriler yüklenemedi.')
        console.error('Kategoriler yüklenirken hata oluştu:', error)
        if (error.response && error.response.status === 401) {
          this.$router.push({ name: 'login' })
        }

        return []
      } finally {
        this.loading = false
      }
    },
    formatDate(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp)

      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    editItem(item) {
      this.formError = ''
      this.editedIndex = item.id
      this.editedItem = this.normalizeCategoryItem({ ...item })
      this.imagePreview = null
      this.selectedImage = null
      this.dialog = true
    },
    close() {
      if (this.saving) return
      this.dialog = false
      this.selectedImage = null
      this.imagePreview = null
      setTimeout(() => {
        if (this.dialog) return
        this.editedItem = { ...this.defaultItem }
        this.editedIndex = -1
      }, 300)
    },
    onImageSelected(event) {
      const file = event.target.files[0]
      if (file) {
        this.selectedImage = file
        const reader = new FileReader()
        reader.onload = e => {
          this.imagePreview = e.target.result
        }
        reader.readAsDataURL(file)
      }
    },
    async save() {
      if (this.saving) return
      this.formError = ''
      const name = String(this.editedItem.name || '').trim()
      if (!name) {
        this.formError = 'Kategori adı zorunlu.'

        return
      }
      this.saving = true
      try {
        const response = this.editedIndex > -1
          ? await axios.put(`${process.env.VUE_APP_BASE_API_URL}category/${this.editedItem.id}`, { name }, this.requestOptions)
          : await axios.post(`${process.env.VUE_APP_BASE_API_URL}category`, { name }, this.requestOptions)
        const savedId = Number(response.data?.data?.id || (this.editedIndex > -1 ? this.editedItem.id : 0))
        if (!Number.isSafeInteger(savedId) || savedId <= 0) {
          this.formError = 'Kayıt sonucu doğrulanamadı. Yeni kategori oluşturmadan önce listeyi yenileyin.'
          this.dialog = false
          this.loadError = this.formError

          return
        }
        this.editedItem.id = savedId
        this.editedIndex = savedId
        if (this.selectedImage) {
          await this.uploadImage(savedId)
          this.selectedImage = null
        }
        this.saving = false
        this.close()
        await this.fetchCategories()
      } catch (error) {
        this.formError = resolveApiErrorMessage(error, this.editedIndex > -1 ? 'Kategori veya görsel güncellenemedi. Aynı kayıt üzerinden tekrar deneyin.' : 'Kategori kaydedilemedi.')
      } finally { this.saving = false }
    },
    async uploadImage(categoryId) {
      const formData = new FormData()
      formData.append('image', this.selectedImage)
      await axios.post(
        `${process.env.VUE_APP_BASE_API_URL}category/addCategoryImage?category_id=${categoryId}`,
        formData,
        this.requestOptions,
      )
    },
    async openDeleteDialog(item) {
      this.deleteItem = item
      this.deleteDialog = true
      this.deleteError = ''
      this.usageBlocked = true
      this.usageMessage = 'Kullanım bilgisi kontrol ediliyor...'
      try {
        const response = await axios.get(`${process.env.VUE_APP_BASE_API_URL}category/${item.id}/usage`, this.requestOptions)
        if (this.deleteItem.id !== item.id) return
        const count = response.data?.data?.linkedCampaigns
        if (!Number.isInteger(count)) throw new Error('Invalid category usage')
        this.usageBlocked = count > 0
        this.usageMessage = `${count} bağlı kampanya.${count ? ' Kullanımdaki kategori silinemez.' : ''}`
      } catch (error) { this.deleteError = resolveApiErrorMessage(error, 'Kullanım bilgisi doğrulanamadı; silme kapalı.') }
    },
    closeDeleteDialog() {
      if (this.deleting) return
      this.deleteDialog = false
      this.deleteItem = {}
    },
    async confirmDelete() {
      if (this.deleting || this.usageBlocked) return
      this.deleting = true
      this.deleteError = ''
      try {
        await axios.delete(
          `${process.env.VUE_APP_BASE_API_URL}category/${this.deleteItem.id}`,
          this.requestOptions,
        )
        this.deleting = false
        this.closeDeleteDialog()
        await this.fetchCategories()
      } catch (error) {
        this.deleteError = resolveApiErrorMessage(error, 'Kategori silinemedi.')
      } finally { this.deleting = false }
    },
  },
}
</script>
